import React, { useState } from 'react';
import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Repeat } from 'lucide-react';

import { logoutUser } from 'store/actions/Auth';
import capitalize from 'utils/capitalize';
import { AuthHelper } from 'utils/helpers/auth.helper';
import { useSwitchApp } from 'hooks/useSwitchApp';

const FONT = 'Inter, sans-serif';

const ellipsis = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};

const itemSx = {
    px: '20px',
    py: '10px',
    gap: '12px',
    display: 'flex',
    alignItems: 'center',
    '&:hover': { bgcolor: '#f9f9f9' },
    // MUI gives a divider that follows a MenuItem 8px of vertical margin, which pushes the
    // item above it up and the one below it down; the dividers here sit flush.
    '& + .MuiDivider-root': { my: 0 }
};

function MenuAction({ icon, label, onClick, disabled }) {
    return (
        <MenuItem onClick={onClick} disabled={disabled} sx={itemSx}>
            <Box sx={{ display: 'flex', color: '#596470' }}>{icon}</Box>
            <Typography
                sx={{
                    fontFamily: FONT,
                    fontWeight: 500,
                    fontSize: '13px',
                    color: '#212322'
                }}
            >
                {label}
            </Typography>
        </MenuItem>
    );
}

// The signed-in user's name at the top right of the resident app, like the operator app's
// profile. Its menu holds the switch to the operator app, which only someone who is both a
// resident and a team member sees, and logout. Styled like the property menu next to it.
export function ResiUserMenu() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userData = useSelector(state => state.user.userData);
    const { canSwitch, label, switching, switchApp } = useSwitchApp();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const names = [userData?.first_name, userData?.last_name].filter(Boolean).map(capitalize);
    const name = names.join(' ');
    const initials = names
        .map(part => part[0])
        .join('')
        .toUpperCase();

    const handleClose = () => setAnchorEl(null);

    const handleSwitch = () => {
        handleClose();
        switchApp();
    };

    const handleLogout = async () => {
        handleClose();
        await AuthHelper.logoutUser();
        dispatch(logoutUser());
        navigate('/login', { state: { from: '' } });
    };

    return (
        <>
            <Button
                id="resi_user_button"
                aria-label="open profile"
                aria-haspopup="true"
                aria-controls={open ? 'resi-user-menu' : undefined}
                onClick={event => setAnchorEl(event.currentTarget)}
                sx={{
                    textTransform: 'none',
                    color: '#212322',
                    gap: '8px',
                    px: '6px',
                    py: '4px',
                    minWidth: 0,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: '#f8f8f8' }
                }}
            >
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: '#ecf1f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}
                >
                    <Typography
                        sx={{ fontSize: '11px', fontWeight: 500, color: '#596470' }}
                    >
                        {initials}
                    </Typography>
                </Box>
                <Typography
                    data-testid="resi-user-name"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        maxWidth: 160,
                        fontFamily: FONT,
                        fontWeight: 500,
                        fontSize: '13px',
                        ...ellipsis
                    }}
                >
                    {name}
                </Typography>
                <ChevronDown
                    size={14}
                    color="#8c8c8c"
                    style={{
                        transition: 'transform 150ms ease',
                        transform: open ? 'rotate(180deg)' : 'none'
                    }}
                />
            </Button>
            <Menu
                id="resi-user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            boxShadow: '0px 4px 8px 0px rgba(0,0,0,0.15)',
                            width: 240,
                            mt: 1
                        }
                    }
                }}
                MenuListProps={{ sx: { p: 0 } }}
            >
                <Box sx={{ px: '20px', py: '12px' }}>
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontWeight: 600,
                            fontSize: '13px',
                            color: '#212322',
                            ...ellipsis
                        }}
                    >
                        {name}
                    </Typography>
                    {userData?.email && (
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontSize: '12px',
                                color: '#8c8c8c',
                                mt: '2px',
                                textTransform: 'none',
                                ...ellipsis
                            }}
                        >
                            {userData.email.toLowerCase()}
                        </Typography>
                    )}
                </Box>
                <Divider sx={{ borderColor: '#f0f0f0' }} />
                {canSwitch && (
                    <MenuAction
                        icon={<Repeat size={16} />}
                        label={label}
                        onClick={handleSwitch}
                        disabled={switching}
                    />
                )}
                {canSwitch && <Divider sx={{ borderColor: '#f0f0f0' }} />}
                <MenuAction icon={<LogOut size={16} />} label="Logout" onClick={handleLogout} />
            </Menu>
        </>
    );
}

export default ResiUserMenu;
